import type { AxiosError } from 'axios';
import { t } from 'i18next';
import { createMutation } from 'react-query-kit';

import type { Mileage } from '@/stores/use-subscription-store';

import { client } from '../common';
import type { Discount, Extra } from '../reservations';
import type { Subscription } from './types';

type CreateSubVariables = {
  model: string;
  trim: string;
  address: string;
  renewalPeriod: string;
  startTime: string;
  autopilot: Extra;
  mileage: Mileage;
  insuranceId: number;
  discount?: Discount;
  customPickupLocation: string;
  customReturnLocation: string;
};
export type CreateSubResponse = {
  subscription: Subscription;
  stripeSecret: string;
  ephemeralKey: string;
  customerSessionSecret: string;
  stripeCustomerId: string;
};

type CreateSubPaymentVariables = {
  id: number;
};
export type CreateSubPaymentResponse = {
  stripeSecret: string;
};

export const useSubscriptionCreate = createMutation<
  CreateSubResponse,
  CreateSubVariables,
  AxiosError
>({
  mutationFn: async (variables) => {
    const formData = new FormData();
    formData.append('model', variables.model);
    formData.append('trim', variables.trim);
    formData.append('address', variables.address);
    formData.append('renewal_period', variables.renewalPeriod);
    formData.append('start_time', variables.startTime);
    if (variables.autopilot.id) {
      formData.append('autopilot', variables.autopilot.id?.toString());
    }
    const isUnlimitedMileage =
      variables.mileage.amount === -1 ||
      variables.mileage.name.toLowerCase().startsWith('unlimited');

    formData.append(
      'mileage',
      isUnlimitedMileage ? '-1' : variables.mileage.name.split(' ')[0]
    );
    formData.append('insurance_id', variables.insuranceId.toString());
    if (variables.discount) {
      formData.append('discount_code', variables.discount.code);
    }
    formData.append('custom_pickup_location', variables.customPickupLocation);
    formData.append('custom_return_location', variables.customReturnLocation);

    return client({
      url: 'v2/subscriptions',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((response) => response.data.data)
      .catch((err: any) => {
        const errors: string[] = err.response.data.errors;
        if (
          err.response.status === 422 &&
          errors.includes(
            'No cars are currently available for that model and trim.'
          )
        ) {
          throw new Error(t('subscriptions.optionUnavailable'));
        }
        throw new Error(errors.join(', '));
      });
  },
});

export const useSubscriptionPaymentCreate = createMutation<
  CreateSubPaymentResponse,
  CreateSubPaymentVariables,
  AxiosError
>({
  mutationFn: async (variables) =>
    client({
      url: `v2/subscriptions/${variables.id}/payment-intent`,
      method: 'POST',
    })
      .then((response) => response.data.data)
      .catch((err: any) => {
        throw new Error(err.response.data.errors.join(', '));
      }),
});
